/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  AppCSS,
  Lbl,
  Spacer,
  TxtInput,
  TapButton,
} from "../../../../components";
import { RowDiv, ColDiv } from "../../../../components/misc/misc.styled";
import { Customer, Progress, Project } from "../../../../redux/types";
import { AssignToProject, EditCustomer } from "../../../../types/graphQlQuery.interface";
import { FC, useEffect, useState } from "react";
import { Prompt } from "../../../../components/dialogue/Dialogue";
import { EditProgressSteps } from "../../../../redux/api";
import { useMutation } from "@apollo/client";
import { ASSIGN_ROLE_TO_PROJECT, EDIT_CUSTOMER } from "../../../../apolloClient/operation/mutations/mutation";
import { toast } from "react-toastify";
import { useFetchAdminUser } from "../../../../apolloClient/hooks/user/fetchHooks";
import { useParams } from "react-router-dom";
import { AdminUser } from "../../../../redux/types/projectDetails";
import { useAppSelector } from "../../../../redux/hooks";
import { Box } from "@mui/material";

enum Role {
  SALES = "SALES",
  ADMIN = "ADMIN",
  USER = "USER",
  AMBASSADOR = "AMBASSADOR",
  PROJECTMANAGER = "PROJECTMANAGER",
  CONCIERGE = "CONCIERGE",
  PROJECTSUPERVISOR = "PROJECTSUPERVISOR",
  PROJECTADMIN = "PROJECTADMIN"
}

interface CustomerProps {
  onFetchProgress: Function;
  progress?: Progress[];
  project?: Project;
  customerDetails?: Customer;
  refetchCustomerData?: Function;
  adminUserdata?: Array<{
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
  }>;
  rolesData?: AdminUser[];
}

interface StepChangeAction {
  step: string;
  forward: boolean;
}

export const CustomerDetails: FC<CustomerProps> = ({
  rolesData,
  onFetchProgress,
  project,
  customerDetails,
  refetchCustomerData,
}) => {
  // customer details
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [cityName, setCityName] = useState("");
  const [stateName, setStateName] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [email, setEmail] = useState("");

  const [isEdit, setIsEdit] = useState(false);
  // Project owner role dropdown
  const projectOwnerRoles = [
    "Ambassador",
    "Project Manager",
    "Concierge",
    "Project Supervisor",
    "Project Admin",
  ];
  // State for each dropdown
  const [selectedRoles, setSelectedRoles] = useState<string[]>(Array(5).fill(""));

  const [openConfirm, setOpenConfirm] = useState(false);
  const [stepToChange, setStepToChange] = useState<StepChangeAction>();
  const [stepChangeMsg, setStepChangeMsg] = useState(
    "This will revert or advance the project to the selected step."
  );

  let { id } = useParams();

  const { loading, error, adminUsersData, refetch } = useFetchAdminUser({
    limit: 100,
    offset: 0,
  });

  // Check if user has PROJECT_ASSIGNMENT permission
  const permissions = useAppSelector((state) => state.userReducer.permissions);
  const hasProjectAssignmentPermission = permissions.some(
    (permission) => permission.name === "PROJECT_ASSIGNMENT" && permission.permission.includes("WRITE")
  );

  // Add mutation hook
  const [editCustomer, { loading: editingCustomer }] = useMutation<
    { editCustomer: Customer },
    { input: EditCustomer }
  >(EDIT_CUSTOMER);

  const [assignRoleToProject, { loading: assigningRole }] = useMutation<
    { AssignRoleToProject: boolean },
    { input: AssignToProject }
  >(ASSIGN_ROLE_TO_PROJECT);

  const handleRoleChange = async (index: number, adminId: string) => {
    if (!adminId) return;
    
    const roleMap = {
      0: Role.AMBASSADOR,
      1: Role.PROJECTMANAGER,
      2: Role.CONCIERGE,
      3: Role.PROJECTSUPERVISOR,
      4: Role.PROJECTADMIN
    };
    
    try {
      await assignRoleToProject({
        variables: {
          input: {
            projectId: Number(id),
            adminId: parseInt(adminId),
            role: roleMap[index]
          }
        }
      });

      const updated = [...selectedRoles];
      updated[index] = adminId;
      setSelectedRoles(updated);
      
      toast("Role assigned successfully", { type: "success" });
      refetchCustomerData();
    } catch (error) {
      // console.error("Error assigning role:", error);
      toast("Error assigning role", { type: "error" });
    }
  };

  useEffect(() => {
    if (customerDetails) {
      const { firstName, lastName, phone, email, address } = customerDetails;

      if (firstName) {
        setFirstName(firstName);
      }
      if (lastName) {
        setLastName(lastName);
      }
      if (phone) {
        setPhoneNumber(phone);
      }
      if (email) {
        setEmail(email);
      }

      if (address && address.length > 0) {
        const { street: streetAddress, city, state, zipCode } = address[0];

        if (streetAddress) {
          setStreetAddress(streetAddress);
        }
        if (city) {
          setCityName(city);
        }
        if (state) {
          setStateName(state);
        }
        if (zipCode) {
          setZipCode(zipCode);
        }
      }
    }
  }, [customerDetails]);

  // Update the selectedRoles initialization
  useEffect(() => {
    if (rolesData) {
      const initialRoles = Array(5).fill("");
      rolesData.forEach(user => {
        switch (user.role) {
          case Role.AMBASSADOR:
            initialRoles[0] = user.id.toString();
            break;
          case Role.PROJECTMANAGER:
            initialRoles[1] = user.id.toString();
            break;
          case Role.CONCIERGE:
            initialRoles[2] = user.id.toString();
            break;
          case Role.PROJECTSUPERVISOR:
            initialRoles[3] = user.id.toString();
            break;
          case Role.PROJECTADMIN:
            initialRoles[4] = user.id.toString();
            break;
        }
      });
      setSelectedRoles(initialRoles);
    }
  }, [rolesData]);

  // Phone number formatting function
  const formatPhoneNumber = (value: string) => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, '');

    // Apply formatting based on length
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  // Handle phone number change with formatting
  const handlePhoneNumberChange = (value: string) => {
    const formatted = formatPhoneNumber(value);
    setPhoneNumber(formatted);
  };

  const onTapUpdateProfile = async () => {
    if (isEdit) {
      // Save the changes
      try {
        await editCustomer({
          variables: {
            input: {
              customerId: customerDetails?.id || 0,
              firstName,
              lastName,
              phone: phoneNumber,
              email,
              addrId: customerDetails?.address?.[0]?.id,
              street: streetAddress,
              city: cityName,
              state: stateName,
              zipCode,
            },
          },
        });

        toast("Customer info updated", { type: "success" });

        // Refetch customer data if callback is provided
        if (refetchCustomerData) {
          refetchCustomerData();
        }
      } catch (error) {
        toast("Error updating customer details. Please try again.", {
          type: "error",
        });
        console.error("Error updating customer:", error);
      }
    }
    setIsEdit(!isEdit);
  };

  const changeCustomerStage = (stage: StepChangeAction) => {
    setStepChangeMsg(
      `This will ${
        stage.forward ? "advance" : "revert"
      } the project to the selected step.`
    );
    setOpenConfirm(true);
    setStepToChange(stage);
  };

  const customerChangeConfirmation = async () => {
    setOpenConfirm(false);
    try {
      const status = await EditProgressSteps(
        project.id,
        stepToChange.step,
        stepToChange.forward ? "forward" : "reverse"
      );
      if (status === 200) {
        onFetchProgress();
      }
      // TODO: show errors
    } catch (error) {}
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", marginBottom: 3 }}>
        <TapButton
          title={isEdit ? "Save" : "Edit"}
          onTap={() => onTapUpdateProfile()}
          bgColor={AppCSS.GLAS_BLUE}
          width={120}
          height={38}
          disable={editingCustomer}
        />
      </Box>
      <Prompt
        message={stepChangeMsg}
        onConfirm={() => {
          customerChangeConfirmation();
        }}
        onClose={() => {
          setStepToChange({} as StepChangeAction);
          setOpenConfirm(false);
        }}
        open={openConfirm}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        {/* Name Fields */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <Box>
            <Box sx={{ fontSize: "13px", color: "#6b7280", marginBottom: "8px", fontWeight: 500 }}>
              First Name
            </Box>
            {isEdit ? (
              <TxtInput
                placeholder="First Name"
                value={firstName}
                onChange={(value) => setFirstName(value)}
                width="100%"
              />
            ) : (
              <Box sx={{ fontSize: "15px", color: "#1f2937" }}>{firstName}</Box>
            )}
          </Box>
          <Box>
            <Box sx={{ fontSize: "13px", color: "#6b7280", marginBottom: "8px", fontWeight: 500 }}>
              Last Name
            </Box>
            {isEdit ? (
              <TxtInput
                placeholder="Last Name"
                value={lastName}
                onChange={(value) => setLastName(value)}
                width="100%"
              />
            ) : (
              <Box sx={{ fontSize: "15px", color: "#1f2937" }}>{lastName}</Box>
            )}
          </Box>
        </Box>

        {/* Phone and Email */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <Box>
            <Box sx={{ fontSize: "13px", color: "#6b7280", marginBottom: "8px", fontWeight: 500 }}>
              Phone Number
            </Box>
            {isEdit ? (
              <TxtInput
                placeholder="(123) 456-7890"
                value={phoneNumber}
                onChange={(value) => handlePhoneNumberChange(value)}
                width="100%"
              />
            ) : (
              <Box sx={{ fontSize: "15px", color: "#1f2937" }}>{phoneNumber}</Box>
            )}
          </Box>
          <Box>
            <Box sx={{ fontSize: "13px", color: "#6b7280", marginBottom: "8px", fontWeight: 500 }}>
              Email
            </Box>
            {isEdit ? (
              <TxtInput
                placeholder="Email"
                value={email}
                onChange={(value) => setEmail(value)}
                width="100%"
              />
            ) : (
              <Box
                sx={{
                  fontSize: "15px",
                  color: "#1f2937",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={email}
              >
                {email}
              </Box>
            )}
          </Box>
        </Box>

        {/* Created By Info - Only shown in view mode */}
        {!isEdit && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              paddingTop: 1,
              borderTop: "1px solid #e5e7eb",
            }}
          >
            <Box>
              <Box sx={{ fontSize: "13px", color: "#6b7280", marginBottom: "8px", fontWeight: 500 }}>
                Created By
              </Box>
              <Box sx={{ fontSize: "15px", color: "#1f2937" }}>
                {customerDetails?.addedBy || "-"}
              </Box>
            </Box>
            <Box>
              <Box sx={{ fontSize: "13px", color: "#6b7280", marginBottom: "8px", fontWeight: 500 }}>
                Created By (Email)
              </Box>
              <Box sx={{ fontSize: "15px", color: "#1f2937" }}>
                {customerDetails?.emailAddedBy || "-"}
              </Box>
            </Box>
          </Box>
        )}

        {/* Street Address */}
        <Box>
          <Box sx={{ fontSize: "13px", color: "#6b7280", marginBottom: "8px", fontWeight: 500 }}>
            Street Address
          </Box>
          {isEdit ? (
            <TxtInput
              placeholder="Street Address"
              value={streetAddress}
              onChange={(value) => setStreetAddress(value)}
              width="100%"
            />
          ) : (
            <Box sx={{ fontSize: "15px", color: "#1f2937" }}>{streetAddress}</Box>
          )}
        </Box>

        {/* City, State, Zip */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr 1fr" },
            gap: 2,
          }}
        >
          <Box>
            <Box sx={{ fontSize: "13px", color: "#6b7280", marginBottom: "8px", fontWeight: 500 }}>
              City Name
            </Box>
            {isEdit ? (
              <TxtInput
                placeholder="City"
                value={cityName}
                onChange={(value) => setCityName(value)}
                width="100%"
              />
            ) : (
              <Box sx={{ fontSize: "15px", color: "#1f2937" }}>{cityName}</Box>
            )}
          </Box>
          <Box>
            <Box sx={{ fontSize: "13px", color: "#6b7280", marginBottom: "8px", fontWeight: 500 }}>
              State Name
            </Box>
            {isEdit ? (
              <TxtInput
                placeholder="State"
                value={stateName}
                onChange={(value) => setStateName(value)}
                width="100%"
              />
            ) : (
              <Box sx={{ fontSize: "15px", color: "#1f2937" }}>{stateName}</Box>
            )}
          </Box>
          <Box>
            <Box sx={{ fontSize: "13px", color: "#6b7280", marginBottom: "8px", fontWeight: 500 }}>
              Zip Code
            </Box>
            {isEdit ? (
              <TxtInput
                placeholder="Zip Code"
                value={zipCode}
                onChange={(value) => setZipCode(value)}
                width="100%"
              />
            ) : (
              <Box sx={{ fontSize: "15px", color: "#1f2937" }}>{zipCode}</Box>
            )}
          </Box>
        </Box>

        {/* Assign Project Owners */}
        <Box sx={{ marginTop: 2, paddingTop: 3, borderTop: "1px solid #e5e7eb" }}>
          <Box sx={{ fontSize: "18px", fontWeight: 600, color: "#1f2937", marginBottom: 2 }}>
            Assign Project Owners
          </Box>
          {hasProjectAssignmentPermission ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr" },
                gap: 2,
              }}
            >
              {projectOwnerRoles.map((role, idx) => (
                <Box key={role}>
                  <Box sx={{ fontSize: "13px", color: "#6b7280", marginBottom: "8px", fontWeight: 500 }}>
                    {role}
                  </Box>
                  <select
                    style={{
                      width: "100%",
                      height: 40,
                      fontSize: 14,
                      padding: "8px 12px",
                      borderRadius: "6px",
                      border: "1px solid #e5e7eb",
                      backgroundColor: "white",
                      cursor: "pointer",
                    }}
                    value={selectedRoles[idx]}
                    onChange={(e) => handleRoleChange(idx, e.target.value)}
                  >
                    <option value="">Select {role}</option>
                    {adminUsersData?.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.firstName} {user.lastName} ({user.email})
                      </option>
                    ))}
                  </select>
                </Box>
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                padding: "16px",
                backgroundColor: "#f3f4f6",
                borderRadius: "8px",
                color: "#6b7280",
                fontSize: "14px",
              }}
            >
              You don't have permission to assign project roles.
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
